import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import TodayIcon from '@mui/icons-material/Today';

import { format, isToday } from 'date-fns';

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';

import { Tooltip } from '@/components/ui/Tooltip';

import { cn } from '@/utils/cn';

import { HrEmployeeScheduleGridSkeleton } from './HrCalendarGridLoader';
import {

  hrDateKey,

  hrFormatHours,

  hrIsWeekendDay,

  hrMonthDaysOnly,

  hrShiftMonth,

  hrWeekdayLabel,

} from './hrCalendarUtils';



export type HrScheduleEmployee = {

  id: number;

  name: string;

  subtitle?: string;

  initials?: string;

};



export interface HrEmployeeScheduleGridProps {

  viewDate: Date;

  onViewDateChange: (date: Date) => void;

  employees: HrScheduleEmployee[];

  loading?: boolean;

  renderCell: (employeeId: number, day: Date, dayKey: string) => ReactNode;

  onCellClick?: (employeeId: number, day: Date) => void;

  getEmployeeTotal?: (employeeId: number) => number | null;

  formatEmployeeTotal?: (employeeId: number, rawTotal: number) => string;

  totalColumnLabel?: string;

  emptyMessage?: string;

  className?: string;

  getCellBackgroundClass?: (employeeId: number, day: Date, dayKey: string) => string | undefined;

  getHeaderBackgroundClass?: (day: Date) => string | undefined;

  getCellTitle?: (employeeId: number, day: Date, dayKey: string) => string | undefined;

  calendarLegend?: ReactNode;

  /** Override visible day columns (month/week/day modes). */
  days?: Date[];

}



const EMPLOYEE_COL = 'min-w-[11rem] w-[11rem]';

const TOTAL_COL = 'min-w-[4.5rem] w-[4.5rem]';

const DAY_COL = 'min-w-[5.5rem] w-[5.5rem]';

const ROW_HEIGHT_PX = 72;

const HEADER_HEIGHT_PX = 52;

const MAX_BODY_HEIGHT_PX = 640;



export function HrEmployeeScheduleGrid({

  viewDate,

  onViewDateChange,

  employees,

  loading,

  renderCell,

  onCellClick,

  getEmployeeTotal,
  totalColumnLabel = 'Total hrs',

  formatEmployeeTotal,

  emptyMessage = 'No employees to display',

  className,

  getCellBackgroundClass,

  getHeaderBackgroundClass,

  getCellTitle,

  calendarLegend,

  days: daysProp,

}: HrEmployeeScheduleGridProps) {

  const days = daysProp ?? hrMonthDaysOnly(viewDate);

  const scrollRef = useRef<HTMLDivElement>(null);

  const [scrollTop, setScrollTop] = useState(0);

  const onBodyScroll = useCallback(() => {
    setScrollTop(scrollRef.current?.scrollTop ?? 0);
  }, []);



  const { startIndex, endIndex, topSpacer, bottomSpacer } = useMemo(() => {

    const overscan = 4;

    const viewportHeight = MAX_BODY_HEIGHT_PX;

    const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT_PX) - overscan);

    const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT_PX) + overscan * 2;

    const end = Math.min(employees.length, start + visibleCount);

    return {

      startIndex: start,

      endIndex: end,

      topSpacer: start * ROW_HEIGHT_PX,

      bottomSpacer: Math.max(0, (employees.length - end) * ROW_HEIGHT_PX),

    };

  }, [employees.length, scrollTop]);



  const visibleEmployees = useMemo(

    () => employees.slice(startIndex, endIndex),

    [employees, startIndex, endIndex],

  );



  return (

    <div className={cn('space-y-3', className)}>

      <div className="flex flex-wrap items-center justify-between gap-3">

        <div className="flex items-center gap-2">

          <Button variant="secondary" size="sm" onClick={() => onViewDateChange(hrShiftMonth(viewDate, -1))}>

            <ChevronLeftIcon fontSize="small" />

          </Button>

          <h4 className="min-w-[9rem] text-center text-sm font-semibold text-slate-900 dark:text-slate-100">

            {format(viewDate, 'MMMM yyyy')}

          </h4>

          <Button variant="secondary" size="sm" onClick={() => onViewDateChange(hrShiftMonth(viewDate, 1))}>

            <ChevronRightIcon fontSize="small" />

          </Button>

          <Button variant="secondary" size="sm" onClick={() => onViewDateChange(new Date())} title="Go to today">

            <TodayIcon fontSize="small" />

          </Button>

        </div>

        <span className="text-xs text-slate-500">{employees.length} employees · {days.length} days</span>

      </div>

      {calendarLegend}



      {loading ? (

        <HrEmployeeScheduleGridSkeleton dayCount={days.length} showTotalColumn={Boolean(getEmployeeTotal)} />

      ) : employees.length === 0 ? (

        <p className="text-sm text-slate-500">{emptyMessage}</p>

      ) : (

        <div className="isolate overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
          <div
            ref={scrollRef}
            className="overflow-y-auto overflow-x-hidden"
            style={{ maxHeight: MAX_BODY_HEIGHT_PX + HEADER_HEIGHT_PX }}
            onScroll={onBodyScroll}
          >
            <div className="flex min-w-0 align-top">
              <div
                className={cn(
                  'z-[1] shrink-0 border-r border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800',
                  EMPLOYEE_COL,
                )}
              >
                <div
                  className="sticky top-0 z-[2] flex items-end border-b border-slate-200 bg-slate-50 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800"
                  style={{ height: HEADER_HEIGHT_PX }}
                >
                  Employees
                </div>
                <div style={{ height: topSpacer }} aria-hidden />
                {visibleEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex h-[4.5rem] items-center gap-2 border-b border-slate-100 bg-slate-50 px-2 dark:border-slate-800 dark:bg-slate-800"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                      {employee.initials ?? '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{employee.name}</div>
                      {employee.subtitle ? (
                        <div className="truncate text-xs text-slate-500">{employee.subtitle}</div>
                      ) : null}
                    </div>
                  </div>
                ))}
                <div style={{ height: bottomSpacer }} aria-hidden />
              </div>

              <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                <div className="inline-flex min-w-full flex-col align-top">
                <div
                  className="sticky top-0 z-[2] flex border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800"
                  style={{ height: HEADER_HEIGHT_PX }}
                >
                  {days.map((day) => {
                    const weekend = !getHeaderBackgroundClass && hrIsWeekendDay(day);
                    const today = isToday(day);
                    const headerBg = getHeaderBackgroundClass?.(day);
                    return (
                      <div
                        key={hrDateKey(day)}
                        className={cn(
                          DAY_COL,
                          'flex flex-col items-center justify-end border-r border-slate-100 pb-1.5 pt-1 text-center dark:border-slate-800',
                          headerBg,
                          !headerBg && weekend && 'bg-slate-100/80 dark:bg-slate-800/50',
                          !headerBg && today && 'bg-primary-50 dark:bg-primary-950/30',
                          !headerBg && !weekend && !today && 'bg-slate-50 dark:bg-slate-800',
                        )}
                      >
                        <span className="text-[10px] font-medium uppercase text-slate-500">{hrWeekdayLabel(day)}</span>
                        <span
                          className={cn(
                            'mt-0.5 text-sm font-semibold',
                            today ? 'text-primary-700 dark:text-primary-300' : 'text-slate-800 dark:text-slate-200',
                          )}
                        >
                          {format(day, 'd')}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div style={{ height: topSpacer }} aria-hidden />
                {visibleEmployees.map((employee) => (
                  <div key={employee.id} className="flex">
                    {days.map((day) => {
                      const dayKey = hrDateKey(day);
                      const weekend = !getCellBackgroundClass && hrIsWeekendDay(day);
                      const today = isToday(day);
                      const clickable = Boolean(onCellClick);
                      const cellBg = getCellBackgroundClass?.(employee.id, day, dayKey);
                      const cellTitle = getCellTitle?.(employee.id, day, dayKey);
                      const cell = (
                        <div
                          className={cn(
                            DAY_COL,
                            'flex h-[4.5rem] flex-col justify-center border-b border-r border-slate-100 p-1 dark:border-slate-800',
                            cellBg,
                            !cellBg && weekend && 'bg-slate-50/90 dark:bg-slate-800/30',
                            !cellBg && today && 'bg-primary-50/50 dark:bg-primary-950/20',
                            clickable && 'cursor-pointer hover:bg-primary-50/80 dark:hover:bg-primary-950/40',
                          )}
                          onClick={clickable ? () => onCellClick?.(employee.id, day) : undefined}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                        >
                          {renderCell(employee.id, day, dayKey)}
                        </div>
                      );
                      return (
                        <Tooltip key={`${employee.id}-${dayKey}`} content={cellTitle}>
                          {cell}
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
                <div style={{ height: bottomSpacer }} aria-hidden />
                </div>
              </div>

              {getEmployeeTotal ? (
                <div
                  className={cn(
                    'z-[1] shrink-0 border-l border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800',
                    TOTAL_COL,
                  )}
                >
                  <div
                    className="sticky top-0 z-[2] flex items-end justify-center border-b border-slate-200 bg-slate-50 px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800"
                    style={{ height: HEADER_HEIGHT_PX }}
                  >
                    {totalColumnLabel}
                  </div>
                  <div style={{ height: topSpacer }} aria-hidden />
                  {visibleEmployees.map((employee) => {
                    const total = getEmployeeTotal(employee.id);
                    const label =
                      total != null && total > 0
                        ? formatEmployeeTotal
                          ? formatEmployeeTotal(employee.id, total)
                          : hrFormatHours(total)
                        : '—';
                    return (
                      <div
                        key={`total-${employee.id}`}
                        className="flex h-[4.5rem] items-center justify-center border-b border-slate-100 bg-slate-50 text-sm font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
                      >
                        {label}
                      </div>
                    );
                  })}
                  <div style={{ height: bottomSpacer }} aria-hidden />
                </div>
              ) : null}
            </div>
          </div>
        </div>

      )}

    </div>

  );

}



export const HR_SHIFT_BLOCK_STYLES = [

  'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100',

  'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100',

  'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100',

  'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-100',

  'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100',

  'border-teal-200 bg-teal-50 text-teal-900 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-100',

] as const;



export function hrShiftBlockStyle(shiftId?: number): string {

  if (shiftId == null) return HR_SHIFT_BLOCK_STYLES[0];

  return HR_SHIFT_BLOCK_STYLES[Math.abs(shiftId) % HR_SHIFT_BLOCK_STYLES.length];

}



export function hrEmployeeInitials(firstName?: string, lastName?: string): string {

  const f = firstName?.trim()?.[0] ?? '';

  const l = lastName?.trim()?.[0] ?? '';

  const initials = `${f}${l}`.toUpperCase();

  return initials || '?';

}


